// Authority ranking for organization roles: who may see and manage whom on the
// Users page. Mirrors backend/src/entities/user/constants/role-hierarchy.constants.ts
// — keep in sync (roleHierarchy.test.ts pins the table by value so drift fails).
//
// NOTE: the UserRoles enum's declaration order is not a ranking — always rank
// through here. Keyed by plain string because the three copies of this table span
// three role enums that don't agree (mobile's omits REVIEWER).
export const ROLE_RANK: Record<string, number> = {
  SUPER_ADMIN: 100,
  OWNER: 80,
  ADMIN: 60,
  PROJECT_MANAGER: 40,
  SUPERINTENDENT: 20,
  CREW_LEAD: 10,
  EMPLOYEE: 5,
  REVIEWER: 1,
  // Sprint 25, story-128. Tied with REVIEWER — both are bottom-of-hierarchy external viewers.
  CLIENT: 1,
};

// Lowest rank that may reach the Users page at all.
export const MANAGE_USERS_MIN_RANK = ROLE_RANK.SUPERINTENDENT;

// Lowest rank that may reach a task-template action (create/import/delete). Story-107.
export const TEMPLATE_ACTIONS_MIN_RANK = ROLE_RANK.SUPERINTENDENT;

// Display labels for EVERY role, including the two the Users page cannot assign.
// UserType (pages/users/constants) only covers the assignable ones, so it renders
// blank for a user who is an OWNER of another organization — which a SUPER_ADMIN
// browsing memberships across tenants will see.
export const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  OWNER: 'Owner',
  ADMIN: 'Admin',
  PROJECT_MANAGER: 'Project Manager',
  SUPERINTENDENT: 'Superintendent',
  CREW_LEAD: 'Crew Lead',
  EMPLOYEE: 'Employee',
  REVIEWER: 'Reviewer',
  CLIENT: 'Client',
};

// Never blank: falls back to the raw role so an unmapped value stays debuggable.
export const roleLabel = (role?: string | null): string =>
  (role && ROLE_LABELS[role]) || role || '—';

// Sprint 29, story-148. Maps each role to its `enumLabels.role*` catalog key. Locale-aware
// counterpart to `roleLabel` above — `roleLabel` stays as-is (English, used where callers have
// not been converted yet); new/converted call sites use this instead, per the same
// non-component-.ts-file factory pattern as `getTranslatedTaskStatusOptions` (`@utils/options`):
// the translated section is passed in by the calling component, never read via a hook here.
const ROLE_LABEL_KEYS: Record<string, string> = {
  SUPER_ADMIN: 'roleSuperAdmin',
  OWNER: 'roleOwner',
  ADMIN: 'roleAdmin',
  PROJECT_MANAGER: 'roleProjectManager',
  SUPERINTENDENT: 'roleSuperintendent',
  CREW_LEAD: 'roleCrewLead',
  EMPLOYEE: 'roleEmployee',
  REVIEWER: 'roleReviewer',
  CLIENT: 'roleClient',
};

// Closes Addendum 9's blank-for-OWNER/SUPER_ADMIN defect and the i18n gap in the same change:
// this reads the complete 9-role map above (not the Users page's narrower 7-role `UserType`),
// so it never renders blank, and it always returns catalog text, never raw English.
export const getTranslatedRoleLabel = (
  role: string | null | undefined,
  tEnumLabels: Record<string, string>,
  unknownLabel: string,
): string => {
  const key = role ? ROLE_LABEL_KEYS[role] : undefined;
  return (key && tEnumLabels[key]) || unknownLabel;
};

// Roles the Users page can represent, descending rank. SUPER_ADMIN and OWNER are
// excluded: they're created via signup / the Super Admin pages, never here.
export const EMPLOYEE_SURFACE_ROLES: string[] = [
  'ADMIN',
  'PROJECT_MANAGER',
  'SUPERINTENDENT',
  'CREW_LEAD',
  'EMPLOYEE',
  'REVIEWER',
];

// Derived so it can never drift from ROLE_RANK.
const ROLES_BY_DESCENDING_RANK: string[] = Object.keys(ROLE_RANK).sort(
  (a, b) => ROLE_RANK[b] - ROLE_RANK[a],
);

// Rank of a role, or -1 for an unknown/absent role (which manages nobody).
export const roleRank = (role?: string | null): number => {
  if (!role) return -1;
  const rank = ROLE_RANK[role];
  return rank === undefined ? -1 : rank;
};

// Whether this role may reach the Users page at all.
export const canManageUsers = (actorRole?: string | null): boolean =>
  roleRank(actorRole) >= MANAGE_USERS_MIN_RANK;

// Whether the actor may view and edit a user with this role. Peers included: a
// Project Manager may edit another Project Manager, but not an Admin.
export const canManageUserRole = (
  actorRole?: string | null,
  targetRole?: string | null,
): boolean => {
  if (!canManageUsers(actorRole)) return false;
  const target = roleRank(targetRole);
  return target >= 0 && target <= roleRank(actorRole);
};

// Whether the actor may CHANGE this user's role. Stricter than
// canManageUserRole: the target must be STRICTLY below the actor, so equals
// cannot demote or promote each other — a Superintendent may edit a peer
// Superintendent's profile but not their role. Since a role never outranks
// itself, this also stops anyone changing their own role.
//
// The set of assignable roles is separate and stays at-or-below
// (getAssignableRoles), so promoting an Employee up to Superintendent is still
// allowed: creating a peer is not managing one.
export const canChangeUserRole = (
  actorRole?: string | null,
  targetRole?: string | null,
): boolean => {
  if (!canManageUsers(actorRole)) return false;
  const target = roleRank(targetRole);
  return target >= 0 && target < roleRank(actorRole);
};

// Every role at or below the actor's level, descending rank.
export const getVisibleUserRoles = (actorRole?: string | null): string[] => {
  if (!canManageUsers(actorRole)) return [];
  const actor = roleRank(actorRole);
  return ROLES_BY_DESCENDING_RANK.filter((role) => ROLE_RANK[role] <= actor);
};

// Roles the actor may actually assign on the Users page: the visible set narrowed
// to what that surface can represent. Drives the role tabs and role dropdown.
export const getAssignableRoles = (actorRole?: string | null): string[] => {
  const visible = getVisibleUserRoles(actorRole);
  return visible.filter((role) => EMPLOYEE_SURFACE_ROLES.includes(role));
};

// Roles that use the mobile app only. Web sends them to /home (a pointer to the app)
// and no other route. Not rank-based: REVIEWER ranks lower but is web-only.
// See "Which surface each role uses" in docs/prd.md.
export const MOBILE_ONLY_ROLES: string[] = ['CREW_LEAD', 'EMPLOYEE'];

export const isMobileOnlyRole = (role?: string | null): boolean =>
  !!role && MOBILE_ONLY_ROLES.includes(role);

// Sprint 25, story-126/127/128. Mirrors backend/src/auth/external-viewer.constants.ts's
// EXTERNAL_VIEWER_ROLES. REVIEWER and CLIENT are both read-only, no-mutation roles — every
// write affordance on web must check this (or the server refuses the mutation, per D1/D2).
export const EXTERNAL_VIEWER_ROLES: string[] = ['REVIEWER', 'CLIENT'];

export const isExternalViewerRole = (role?: string | null): boolean =>
  !!role && EXTERNAL_VIEWER_ROLES.includes(role);

// Sprint 36, story-192 (D2). Mirrors backend/src/common/money/money-posture.ts's
// `moneyPostureForRole` — same three-line body. A plan task's `budget` is COST-side, so it is
// visible/editable only at posture 'ALL'. Do NOT use `isExternalViewerRole` as a proxy for this.
export type MoneyPosture = 'ALL' | 'PRICE' | 'NONE';

export const moneyPostureForRole = (role?: string | null): MoneyPosture => {
  if (role === 'REVIEWER') return 'NONE';
  if (role === 'CLIENT') return 'PRICE';
  return 'ALL';
};
